import React, { useState, useEffect, useContext } from 'react';
import { axiosInstance } from "../axiosInstance";
import {
  Typography,
  Button,
  Box,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../context/AuthContext';
import { ModalContext } from '../context/ModalContext';

const SavedRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [openRecipe, setOpenRecipe] = useState(null); // holds the recipe currently being viewed
  const { accessToken } = useContext(AuthContext);
  const { openInfoModal, openConfirmModal } = useContext(ModalContext);

  // State for second dialog (full-size image)
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupUrl, setImagePopupUrl] = useState(null);

  useEffect(() => {
    fetchSavedRecipes();
    // eslint-disable-next-line
  }, []);

  const fetchSavedRecipes = async () => {
    if (!accessToken) return;
    try {
      const response = await axiosInstance.get('/api/recipes/saved', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setRecipes(response.data);
    } catch (error) {
      console.error("Error fetching saved recipes:", error);
      openInfoModal({
        title: 'Error',
        message: 'Failed to fetch saved recipes. Please try again.'
      });
    }
  };

  const deleteRecipe = async (id) => {
    if (!accessToken) return;
    await axiosInstance.delete(`/api/recipes/saved/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  };

  const handleDeleteRecipe = (id) => {
    openConfirmModal({
      title: 'Delete Recipe?',
      message: 'Are you sure you want to delete this recipe?',
      onConfirm: async () => {
        try {
          await deleteRecipe(id);
          openInfoModal({
            title: 'Deleted',
            message: 'Recipe deleted successfully!',
            onClose: () => {
              fetchSavedRecipes();
            }
          });
        } catch (error) {
          console.error("Error deleting recipe:", error);
          openInfoModal({
            title: 'Error',
            message: 'Failed to delete the recipe. Please try again.'
          });
        }
      }
    });
  };

  const handleViewRecipe = (recipe) => {
    setOpenRecipe(recipe);
  };

  const handleCloseRecipe = () => {
    setOpenRecipe(null);
  };

  // Open the second dialog with full-size image
  const handleThumbnailClick = (imgUrl) => {
    setImagePopupUrl(imgUrl);
    setImagePopupOpen(true);
  };

  // Close the second dialog
  const handleCloseImagePopup = () => {
    setImagePopupUrl(null);
    setImagePopupOpen(false);
  };

  return (
    <Box
      sx={{
        width: { xs: '90%', md: '50%' },
        margin: '0 auto',
        mt: 4,
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Saved Recipes
      </Typography>

      <List>
        {recipes.map((recipe) => (
          <React.Fragment key={recipe.recipeId}>
            <ListItem
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box sx={{ maxWidth: '70%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {recipe.recipeName}
                </Typography>
                <Typography variant="body2">
                  {recipe.introduction || "No introduction available."}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleViewRecipe(recipe)}
                >
                  View
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDeleteRecipe(recipe.recipeId)}
                >
                  Delete
                </Button>
              </Box>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      {/* Dialog for "View" button */}
      <Dialog
        open={Boolean(openRecipe)}
        onClose={handleCloseRecipe}
        fullWidth
        maxWidth="sm"
      >
        {openRecipe && (
          <>
            <DialogTitle>{openRecipe.recipeName}
            <IconButton
          onClick={handleCloseRecipe}
          aria-label="Close recipe"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        </DialogTitle>
            <DialogContent dividers>
              {/* Layout for introduction + optional thumbnail */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: { xs: 'flex-start', sm: 'center' },
                }}
              >
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {openRecipe.introduction}
                </Typography>

                {/* Show thumbnail ONLY if imageUrl is present */}
                {openRecipe.imageUrl && (
                  <img
                    src={openRecipe.imageUrl}
                    alt={openRecipe.recipeName}
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      borderRadius: '8px',
                    }}
                    onClick={() => handleThumbnailClick(openRecipe.imageUrl)}
                  />
                )}
              </Box>

              {/* Ingredients */}
              <Typography variant="subtitle1" sx={{ fontSize: "inherit", fontWeight: 'bold', mt: 2 }}>
                Ingredients
              </Typography>
              {openRecipe.ingredients && openRecipe.ingredients.length > 0 ? (
                <ul>
                  {openRecipe.ingredients.map((ing, i) => (
                    <li key={i}>
                      <Typography variant="body2" sx={{ fontSize: "inherit" }}>{ing} </Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2">No ingredients found.</Typography>
              )}

              {/* Instructions */}
              <Typography variant="subtitle1" sx={{ fontSize: "inherit", fontWeight: 'bold', mt: 2 }}>
                Instructions
              </Typography>
              {Array.isArray(openRecipe.instructions) ? (
                <ol>
                  {openRecipe.instructions.map((step, i) => (
                    <li key={i}>
                      <Typography variant="body2" sx={{ fontSize: "inherit" }}>{step}</Typography>
                    </li>
                  ))}
                </ol>
              ) : (
                <Typography variant="body2" sx={{ fontSize: "inherit" }}>
                  {openRecipe.instructions || "No instructions provided."}
                </Typography>
              )}

              {/* Tips */}
              {openRecipe.tips && openRecipe.tips.length > 0 && (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: "inherit", fontWeight: 'bold', mt: 2 }}
                  >
                    Tips
                  </Typography>
                  <ul>
                    {openRecipe.tips.map((tip, i) => (
                      <li key={i}>
                        <Typography variant="body2" sx={{ fontSize: "inherit" }}>{tip}</Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Friendly closing */}
              {openRecipe.friendly_closing && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {openRecipe.friendly_closing}
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Full-size image dialog */}
      <Dialog
        open={imagePopupOpen}
        onClose={handleCloseImagePopup}
        maxWidth="md"
        fullWidth
        // Remove the "Close on click outside" if you want
      >
        {/* Custom Title with close icon on the top-left */}
        <Box sx={{ position: 'relative', height: '40px', borderBottom: '1px solid #ccc' }}>
          <IconButton
            onClick={handleCloseImagePopup}
            sx={{ position: 'absolute', top: 4, left: 4 }}
            aria-label="close image"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <DialogContent dividers sx={{ p: 0 }}>
          {/* Clicking the image also closes the dialog */}
          {imagePopupUrl && (
            <img
              src={imagePopupUrl}
              alt="Full Size"
              style={{ width: '100%', cursor: 'pointer' }}
              onClick={handleCloseImagePopup}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SavedRecipes;
